<template>
  <div v-if="hasEnoughData" class="tree-project-tracker">
    <EHeading as="h3" class="tree-project-tracker__heading">
      <ETreeIcon /> {{ $t('web.tree.tracker.title', { countryName }) }}
    </EHeading>
    <p class="tree-project-tracker__intro">
      {{ introText }}
    </p>
    <div class="tree-project-tracker__stats">
      <div v-if="treesPlanted" class="tree-project-tracker__stat">
        <data
          class="tree-project-tracker-stat__number"
          :value="treesPlanted"
          data-test-id="trees-planted"
        >
          {{ prettyTreesPlanted }}
        </data>
        <span>{{ $t('web.tree.tracker.trees') }}</span>
      </div>
      <div v-if="hectaresRestored" class="tree-project-tracker__stat">
        <data
          class="tree-project-tracker-stat__number"
          :value="hectaresRestored"
          data-test-id="hectares-planted"
        >
          {{ prettyHectaresRestored }}
        </data>
        <span>{{ $t('web.tree.tracker.hectares.restored') }}</span>
      </div>
    </div>
    <p class="tree-project-tracker__cta">
      <EButton
        v-if="url"
        as="a"
        :href="url"
        size="s"
        variant="outline"
        target="_blank"
        rel="noopener"
        data-test-id="tree-project-tracker-level-link"
        :aria-label="ctaLabel"
      >
        {{ $t('web.tree.tracker.learn.more') }}
      </EButton>
    </p>
    <div
      v-if="cappedSpecies && cappedSpecies.length > 1"
      class="tree-project-tracker__species"
    >
      <EHeading as="h4" class="tree-project-tracker__heading">
        <span>{{ $t('web.tree.tracker.top.trees') }}</span>
      </EHeading>
      <div class="tree-project-tracker-species__list">
        <ETreeItem
          v-for="tree in cappedSpecies"
          :key="treeId(tree)"
          :title="tree.name"
          :thumbnail="tree.thumbnail"
          :is-native="tree.native"
          :is-endangered="tree.endangered"
        >
          {{ tree.name }}
        </ETreeItem>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import EHeading from '~~/common/vue/components/heading';
import EButton from '~~/common/vue/components/button';
import ETreeIcon from '~~/common/vue/components/icons/m/tree-color.svg';
import ETreeItem from './tree-item';
import plantingCountries from './data.json';
import { localisePlainNumber } from '~~/common/js/universal/intl';

const roundThousands = (number) => Math.floor(number / 1000) * 1000;

export default {
  name: 'ETreeProjectTrackerContent',
  components: {
    EHeading,
    EButton,
    ETreeIcon,
    ETreeItem,
  },
  props: {
    countryName: {
      type: String,
      required: true,
    },
  },
  data() {
    return plantingCountries[this.countryName.toLowerCase()] || {};
  },
  computed: {
    ...mapState('global', ['locale']),
    readMoreLabel() {
      return this.$t('web.tree.tracker.read.more.full');
    },
    ctaLabel() {
      return this.$t('web.tree.tracker.learn.more.full');
    },
    introText() {
      return this.intro || this.$t('web.tree.tracker.intro');
    },
    hasEnoughData() {
      return this.url && this.numPartners > 0;
    },
    country() {
      return this.countryName;
    },
    prettyTreesPlanted() {
      const count = roundThousands(this.treesPlanted);
      return `${localisePlainNumber(this.locale, count)}`;
    },
    prettyHectaresRestored() {
      return `${localisePlainNumber(this.locale, this.hectaresRestored)}`;
    },
    cappedSpecies() {
      return this.species?.slice(0, 3) || [];
    },
  },
  methods: {
    treeId({ name }) {
      return name.toLowerCase().replace(' ', '_');
    },
  },
};
</script>

<style lang="scss" scoped>
.tree-project-tracker {
  @include divider(top);

  margin-top: $space-m;
}

.tree-project-tracker__heading {
  font-size: $font-1l;
  font-weight: $font-weight-l;
  margin: $space-l 0;
}

.tree-project-tracker__intro {
  @include line-clamp(4);
}

.tree-project-tracker__stats {
  display: flex;
}

.tree-project-tracker__stat {
  display: flex;
  flex-direction: column;
  border-radius: 64px;
  flex-grow: 1;

  svg {
    height: 50px;
  }
}

.tree-project-tracker-stat__number {
  font-size: $font-3l;
  font-weight: $font-weight-1l;
  display: inline-flex;
}

.tree-project-tracker__species {
  @include divider(top);

  margin-top: $space-l;
}

.tree-project-tracker-species__list {
  display: flex;
  overflow-x: auto;
  margin: 0 -20px;
  padding: 0 20px;
}
</style>
